'use client';

import {useEffect, useRef} from 'react';
import {usePathname} from 'next/navigation';
import {datadogRum} from '@datadog/browser-rum';
import getGameId from '@/utils/getGameId';
import TagManager from '@sooro-io/react-gtm-module';
type Props = {gtmId: string; env: string; nonce: string; appsFlyerKey: string};

export default function ClientSideHead({gtmId, env, nonce, appsFlyerKey}: Props) {
  const pathname = usePathname();
  const datadogRumInitialized = useRef(false);

  useEffect(() => {
    if (gtmId && nonce) {
      TagManager.initialize({gtmId, nonce});
    }
  }, [gtmId, nonce]);

  // TODO - Add User Consent for Gtag and Datadog
  useEffect(() => {
    // Initialize Datadog RUM
    if (!datadogRumInitialized.current) {
      datadogRumInitialized.current = true;
      datadogRum.init({
        applicationId: '3c961121-207b-4931-8de1-3bd8971abc83',
        clientToken: 'pub906b17ee3c0f14eb2f836ffd08cc8d31',
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service: 'web-sdk',
        env: env,
        // Specify a version number to identify the deployed version of your application in Datadog
        // version: '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      });

      // Start tracking user sessions
      datadogRum.startSessionReplayRecording();
    }
    // Log a page view on route change
    const gameId = getGameId();
    if (gameId) {
      datadogRum.setGlobalContextProperty('skillz.sdk.game_id', gameId);
    }
    datadogRum.setGlobalContextProperty('path', pathname);
  }, [pathname, env]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    console.log('TagManager page load running');
    // Track page view in GTM
    //@ts-ignore
    window.dataLayer = window.dataLayer || [];
    //@ts-ignore
    window.dataLayer.push({
      event: 'pageview',
      page_path: pathname,
    });
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    console.log('Initializing AppsFlyer');

    // Injecting the full AppsFlyer script into the DOM
    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.text = `
      !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
      (t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
      t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
      o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
      p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","pba",{pba: {webAppId: "${appsFlyerKey}", measurementStatus:true}});
    `;

    if (nonce) {
      script.nonce = nonce;
    }

    document.head.appendChild(script);
  }, [nonce]);

  return <></>;
}
